import DeleteConfirmDialog from '@/components/ui/dialog/delete-confirm-dialog';
import { PDKDeleteIcon } from '@/components/ui/pdk-icons';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from '@/hooks/use-toast';
import { isImage, isPdf } from '@/lib/get-file-type';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn, formatBytes } from '@/lib/utils';
import { Deal, FileMetadata } from '@/models/registry';
import { fileMetadataStore } from '@/models/stores/index';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { debounce, isEmpty } from 'lodash';
import {
  FileCode,
  FileIcon,
  FileImage,
  FileSpreadsheet,
  FileText,
  Loader2,
} from 'lucide-react';
import { observer } from 'mobx-react';
import { useMemo, useState } from 'react';
import DialogImagePdfCarousel from './dialog-image-pdf-carousel';
import FileNameEditor from './file-name-editor';

const Files = observer(
  ({ deal, className }: { deal: Deal; className?: string }) => {
    const dealShortId = deal.short_id;

    // Function to get the appropriate icon based on MIME type
    const getFileIcon = (mimeType: string) => {
      switch (mimeType) {
        case 'image/png':
        case 'image/jpeg':
          return <FileImage className="h-4 w-4 text-blue-500" />;
        case 'application/pdf':
          return <FileIcon className="h-4 w-4 text-red-500" />;
        case 'text/csv':
          return <FileSpreadsheet className="h-4 w-4 text-green-500" />;
        case 'text/plain':
          return <FileText className="h-4 w-4 text-gray-500" />;
        default:
          return <FileCode className="h-4 w-4 text-purple-500" />;
      }
    };

    const files = fileMetadataStore.getByDealId(deal.id);

    const imageOrPdfFiles = useMemo(() => {
      return files.filter((file) => isImage(file) || isPdf(file)) || [];
    }, [files]);

    const [isSaving, setIsSaving] = useState<FileMetadata['id'] | null>(null);

    const [fileToDelete, setFileToDelete] = useState<FileMetadata | null>(null);

    const [fileToViewIndex, setFileToViewIndex] = useState<number | null>(null);
    const fileToView: FileMetadata | null = useMemo(() => {
      if (fileToViewIndex === null) return null;

      return imageOrPdfFiles[fileToViewIndex];
    }, [imageOrPdfFiles, fileToViewIndex]);

    const queryClient = useQueryClient();
    // /:file_id/delete
    const deleteFileMutation = useMutation({
      onMutate: async ({ file }) => {
        fileMetadataStore.remove(file.id);
      },
      mutationFn: async ({ file }: { file: Deal['files'][number] }) => {
        const fileId = file.id;
        if (!fileId) return;

        // /:id/files/:file_id/edit
        const response = await pdkAxios
          .post(`/deals/${deal.id}/files/${fileId}/delete`)
          .catch((e) => {
            console.error('Error updating file name', e);
            throw e;
          });

        return response.data;
      },
      onSuccess: () => {
        // toast({
        //   title: `Successfully deleted file`,
        //   description: file?.name,
        // });
        setFileToDelete(null);
      },
      onError: (error) => {
        toast({
          title: 'Error deleting file',
          description: error.message,
        });

        console.error('Error deleting file', error);
      },
      onSettled: () => {
        // onSettled?.();
        queryClient.invalidateQueries({ queryKey: ['deal', dealShortId] });
      },
    });

    if (isEmpty(files)) {
      return (
        <p className="text-sm text-gray-500">No files attached to this deal.</p>
      );
    }

    return (
      <div className={cn('space-y-4 mb-200 w-full', className)}>
        <div className="space-y-2">
          <ul className="space-y-2">
            {files.map((file: FileMetadata) => (
              <li key={file.id}>
                <div className="group flex flex-row gap-1 items-center justify-between w-full">
                  <div
                    className="flex flex-row gap-1 items-center "
                    onClick={() => {
                      // If file type is not an image open a new tab
                      if (!isImage(file) && !isPdf(file)) {
                        window.open(file.url, '_blank');
                        return;
                      }

                      const indexFromImageFiles = imageOrPdfFiles.findIndex(
                        (imageFile) => imageFile.id === file.id,
                      );

                      setFileToViewIndex(indexFromImageFiles);
                    }}
                  >
                    <div className="flex items-center space-x-2 cursor-pointer">
                      {isSaving === file.id ? (
                        <Loader2 className="size-4 animate-spin text-gray-400" />
                      ) : (
                        getFileIcon(file.type)
                      )}
                    </div>

                    <Tooltip delayDuration={500}>
                      <TooltipTrigger>
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <FileNameEditor
                            dealId={deal.id}
                            dealShortId={deal.short_id}
                            file={file}
                            onStartSave={() => {
                              setIsSaving(file.id);
                            }}
                            onSettled={debounce(
                              () => {
                                setIsSaving(null);
                              },
                              1000,
                              { trailing: true },
                            )}
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="flex flex-row gap-1">
                        <span className="text-xs text-gray-500">
                          {formatBytes(file.size)}
                        </span>
                        <span className="text-xs text-gray-500">
                          {file.type}
                        </span>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div
                    className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded"
                    onClick={() => {
                      setFileToDelete(file);
                    }}
                  >
                    <PDKDeleteIcon className="size-4 text-gray-500 cursor-pointer" />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <DeleteConfirmDialog
          onConfirm={() => {
            if (!fileToDelete) {
              console.error('No file id provided');
              return;
            }

            deleteFileMutation.mutate({ file: fileToDelete });
          }}
          dialogIsOpen={!!fileToDelete}
          onOpen={() => setFileToDelete(fileToDelete)}
          onClose={() => setFileToDelete(null)}
          title={`Delete File for ${fileToDelete?.name}`}
          description="Please confirm you would like to delete the file."
        />

        <DialogImagePdfCarousel
          fileToView={fileToView}
          imageFiles={imageOrPdfFiles}
          fileToViewIndex={fileToViewIndex}
          setFileToViewIndex={setFileToViewIndex}
          dealId={deal.id}
          dealShortId={deal.short_id}
        />
      </div>
    );
  },
);

export default Files;
